import { Alert, MenuItem, Radio, Skeleton, TextField, Typography } from '@mui/material';

import { formatExpiry, isStripeConfigured, type Card } from '@/features/payments';
import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';

import { BankTransferFields } from './BankTransferFields';
import { NewCardFields } from './NewCardFields';
import type { PaymentMethod } from './processingFee';
import type { BankTransferDetails } from './submitPayment';
import type { MethodCompletion } from './useMethodCompletion';
import type { MethodFees } from './usePayItem';

/** A small outlined MUI input is 40px tall (8.5px padding either side of a 23px
 *  line box) — the same height the table filter bars pin their controls to. The
 *  placeholder has to match the field exactly, or swapping one for the other
 *  reintroduces the jump it exists to prevent. */
const FIELD_HEIGHT = 40;

interface PaymentMethodPickerProps {
  method: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
  isCardDisabled: boolean;
  cards: Card[];
  isLoadingCards: boolean;
  selectedCard: string;
  onCardChange: (cardId: string) => void;
  cardholderName: string;
  onCardholderNameChange: (name: string) => void;
  saveCard: boolean;
  onSaveCardChange: (save: boolean) => void;
  bankTransfer: BankTransferDetails;
  onBankTransferChange: (details: BankTransferDetails) => void;
  fees: MethodFees;
  completion: MethodCompletion;
  isSubmitting: boolean;
}

/**
 * How the director is paying.
 *
 * Three exclusive methods, each showing what it costs and how long it takes
 * before it is chosen — a director deciding between an instant 2% card fee and
 * a 1.5% transfer that clears in five days needs both figures in front of them,
 * which the legacy modal did give but spread across three stacked panels wider
 * than a phone.
 *
 * Rendered as radios rather than the legacy checkboxes: only one can ever be
 * active, and checkboxes that clear each other are a control lying about what
 * it does. The whole header is the label, so the tap target is the full row
 * rather than a 20px circle.
 */
export const PaymentMethodPicker = (props: PaymentMethodPickerProps) => {
  const { method, isCardDisabled, cards, isLoadingCards, fees, isSubmitting } = props;

  if (!isStripeConfigured) {
    return (
      <Alert severity="warning" className="rounded-lg">
        Payments are unavailable right now. Contact your agent to settle this item.
      </Alert>
    );
  }

  // Checked before anything that branches on the card list, which would
  // otherwise claim there are no saved cards while the request deciding that is
  // still in flight.
  if (isLoadingCards) {
    return (
      <Skeleton
        variant="rounded"
        height={FIELD_HEIGHT * 3}
        role="status"
        aria-label="Loading your payment methods"
        className="rounded-lg"
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {isCardDisabled && (
        <Alert severity="warning" className="rounded-lg">
          <strong>SMSF compliance:</strong> payments from a self-managed super fund must not be
          made by credit card. This one is paid by bank transfer directly from the fund&rsquo;s
          account.
        </Alert>
      )}

      {!isCardDisabled && cards.length > 0 && (
        <MethodOption
          value="savedCard"
          label="Saved credit card"
          timing="Instant"
          fee={fees.card}
          selected={method === 'savedCard'}
          onSelect={props.onMethodChange}
          disabled={isSubmitting}
        >
          <TextField
            select
            fullWidth
            size="small"
            label="Pay with"
            value={props.selectedCard}
            onChange={(event) => {
              props.onCardChange(event.target.value);
            }}
            disabled={isSubmitting}
          >
            {cards.map((card) => (
              <MenuItem key={card.id} value={card.id}>
                {card.brand} •••• {card.last4} — expires {formatExpiry(card)}
              </MenuItem>
            ))}
          </TextField>
        </MethodOption>
      )}

      {!isCardDisabled && (
        <MethodOption
          value="newCard"
          label="New credit card"
          timing="Instant"
          fee={fees.card}
          selected={method === 'newCard'}
          onSelect={props.onMethodChange}
          disabled={isSubmitting}
        >
          <NewCardFields
            cardholderName={props.cardholderName}
            onCardholderNameChange={props.onCardholderNameChange}
            saveCard={props.saveCard}
            onSaveCardChange={props.onSaveCardChange}
            completion={props.completion}
            disabled={isSubmitting}
          />
        </MethodOption>
      )}

      <MethodOption
        value="bankTransfer"
        label="Bank transfer"
        timing="5 business days"
        fee={fees.bankTransfer}
        selected={method === 'bankTransfer'}
        onSelect={props.onMethodChange}
        // NOT disabled by `isCardDisabled`: on an SMSF row this is the only
        // method left, and greying out the sole permitted choice takes it out
        // of the tab order and makes it read as unavailable.
        disabled={isSubmitting}
      >
        <BankTransferFields
          details={props.bankTransfer}
          onChange={props.onBankTransferChange}
          completion={props.completion}
          disabled={isSubmitting}
        />
      </MethodOption>
    </div>
  );
};

interface MethodOptionProps {
  value: PaymentMethod;
  label: string;
  timing: string;
  fee: number;
  selected: boolean;
  onSelect: (method: PaymentMethod) => void;
  disabled: boolean;
  children: React.ReactNode;
}

/**
 * One method: a header that selects it, and the fields it needs once chosen.
 *
 * The fields are mounted only while selected. That is not only tidiness — a
 * mounted Stripe Element keeps reporting completion, so leaving all three
 * mounted would let a half-filled card form satisfy a bank transfer's checks.
 */
const MethodOption = ({
  value,
  label,
  timing,
  fee,
  selected,
  onSelect,
  disabled,
  children,
}: MethodOptionProps) => (
  <div
    className={cn(
      'rounded-lg border transition-colors',
      selected ? 'border-primary-dark' : 'border-divider',
    )}
  >
    <label
      className={cn(
        'flex min-h-11 cursor-pointer items-center gap-2 px-2 py-2',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <Radio
        checked={selected}
        onChange={() => {
          onSelect(value);
        }}
        disabled={disabled}
        name="paymentMethod"
        size="small"
      />

      {/* min-w-0 so a long label truncates instead of pushing the fee off the
          row — at 360px there is no spare width to give away. */}
      <span className="flex min-w-0 flex-1 flex-col">
        <Typography variant="body2" className="font-medium">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {timing}
        </Typography>
      </span>

      <Typography variant="caption" color="text.secondary" className="shrink-0 text-right">
        {fee === 0 ? 'No fee' : `Fee ${formatCurrency(fee)}`}
      </Typography>
    </label>

    {selected && <div className="border-t border-divider p-3">{children}</div>}
  </div>
);
